import React from 'react'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'

import GlobalStyles from '../../../components/component-items/styles'
import { DragNDrop } from '../../../components/component-items/dragndrop'
import {
  faBoxCheck,
  faBoxOpen,
  faClipboardList,
  faCompressArrowsAlt,
  faConveyorBelt,
  faExpandArrowsAlt,
  faTrash,
} from '@fortawesome/pro-duotone-svg-icons'
import { faCheckDouble, faExclamationTriangle, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { displayValue, orderLabel, parsePositiveInteger } from './adapters'
import { ValidationStatusStyled } from './styles'
import {
  CanonicalItem,
  CanonicalOrder,
  ImportStep,
  SchemaField,
  SourceColumn,
  SubmissionResult,
  UploadFormat,
  UploadIssue,
  UploadMapping,
  UploadSchema,
  ValidationResult,
} from './types'

const steps = [
  { key: 'upload', label: 'Upload', icon: faClipboardList },
  { key: 'match', label: 'Match columns', icon: faBoxOpen },
  { key: 'validate', label: 'Validate data', icon: faConveyorBelt },
  { key: 'submitted', label: 'Import', icon: faBoxCheck },
]

const fieldSectionDescriptions: Record<string, string> = {
  Order: 'Order identity and customer-supplied order details.',
  'Shipping Address': 'Recipient and destination fields used to create the order address.',
  Shipment: 'Carrier service, billing controls, and optional requested ship dates.',
  Items: 'Each source row contributes an item to its grouped order.',
}

const orderFieldValue = (order: CanonicalOrder, field: string) =>
  field === 'originator_id' ? order.originator_id : order.fields[field]

const fieldIssues = (issues: UploadIssue[], field: string) =>
  issues.filter((issue) => issue.field_path === field || issue.field_path.endsWith(`.${field}`))

const unmatchedOrderIssues = (order: CanonicalOrder, fields: SchemaField[]) =>
  order.issues.filter(
    (issue) =>
      !issue.item_id &&
      !issue.field_path.startsWith('items') &&
      !fields.some((field) => fieldIssues([issue], field.key).length)
  )

const shipmentSubsection = (field?: SchemaField) => {
  if (!field) return ''
  if (field.shipment_subsection) return field.shipment_subsection
  if (['ship_method', 'transport_mode'].includes(field.key)) return 'Carrier & Service'
  if (['billing_method', 'billing_account', 'contracting_party'].includes(field.key)) return 'Billing & Control'
  if (['ship_after', 'ship_before'].includes(field.key)) return 'Ship Dates'
  return ''
}

type SourceOption = { value: number; label: string }

export const WorkflowProgress = ({ step }: { step: ImportStep }) => {
  const activeStepIndex = steps.findIndex((item) => item.key === step)
  return (
    <div className="workflow-progress">
      <ul>
        {steps.map((item, index) => (
          <li
            key={item.key}
            className={`${index === activeStepIndex ? 'active' : ''} ${index < activeStepIndex ? 'complete' : ''}`}
          >
            <span className="step-card">
              <FontAwesomeIcon className="icon" icon={item.icon} />
            </span>
            <span className="text">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

type UploadStepProps = {
  file: File | null
  columns: SourceColumn[]
  dataRowCount: number
  inferredFormat: UploadFormat | null
  format: UploadFormat | null
  busy: boolean
  schemaReady: boolean
  onSetFile: (file: File | null) => void
  onChangeFormat: (format: UploadFormat) => void
  onContinue: () => void
}

export const UploadStep = ({
  file,
  columns,
  dataRowCount,
  inferredFormat,
  format,
  busy,
  schemaReady,
  onSetFile,
  onChangeFormat,
  onContinue,
}: UploadStepProps) => (
  <>
    <div className="importer-body">
      <div className="upload-panel">
        <h4>Upload CSV</h4>
        <p className="muted">
          Start by uploading the customer order file. Kase will detect its format from the headers.
        </p>
        <DragNDrop file={file} setFile={onSetFile} fileTypes=".csv" size={10} />
        {file ? (
          <div className="file-meta">
            <strong>{file.name}</strong>
            <p className="muted" style={{ margin: '0.35rem 0' }}>
              {dataRowCount.toLocaleString()} data rows · {columns.length.toLocaleString()} columns
            </p>
            <Form.Group>
              <Form.Label>Detected format</Form.Label>
              <Form.Select
                aria-label="Detected format"
                value={format || ''}
                disabled={busy}
                onChange={(event) => onChangeFormat(event.target.value as UploadFormat)}
              >
                <option value="standard">Standard Kase template</option>
                <option value="configurable">Configurable item-row</option>
              </Form.Select>
              {format !== inferredFormat ? <small className="conditional-label">Format manually changed</small> : null}
            </Form.Group>
          </div>
        ) : (
          <div className="file-meta">
            <strong>Current order template</strong>
            <p className="muted" style={{ margin: '0.35rem 0' }}>
              Use one row per order item. Repeated order IDs are grouped into one order during validation.
            </p>
            <a className="template-link" href="/api/static/templates/order_upload_template.csv">
              Download template
            </a>
          </div>
        )}
      </div>
    </div>
    <div className="importer-footer">
      {file ? (
        <button type="button" className="plain-action" disabled={busy} onClick={() => onSetFile(null)}>
          Remove file
        </button>
      ) : null}
      <GlobalStyles.Button disabled={!file || busy || (format === 'configurable' && !schemaReady)} onClick={onContinue}>
        {busy ? 'Loading...' : format === 'standard' ? 'Submit Standard Import' : 'Next'}
      </GlobalStyles.Button>
    </div>
  </>
)

type MatchStepProps = {
  schema: UploadSchema | null
  savedMapping: UploadMapping | null
  mappingSource: 'saved' | 'one_time'
  mappingFields: SchemaField[]
  sections: string[]
  sourceOptions: SourceOption[]
  selection: Record<string, number[]>
  transformHot: boolean
  skippedColumns: SourceColumn[]
  requiredBindingFields: Set<string>
  missingRequiredMappings: SchemaField[]
  mappingNotice: string | null
  busy: boolean
  onSaveMapping: () => void
  onSelectionChange: (fieldKey: string, selection: number[]) => void
  onTransformHotChange: (checked: boolean) => void
  onBack: () => void
  onValidate: () => void
  onMissingRequiredMappings: () => void
}

export const MatchStep = ({
  schema,
  savedMapping,
  mappingSource,
  mappingFields,
  sections,
  sourceOptions,
  selection,
  transformHot,
  skippedColumns,
  requiredBindingFields,
  missingRequiredMappings,
  mappingNotice,
  busy,
  onSaveMapping,
  onSelectionChange,
  onTransformHotChange,
  onBack,
  onValidate,
  onMissingRequiredMappings,
}: MatchStepProps) => (
  <>
    <div className="importer-body">
      <div className="match-list">
        <div className="match-list-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <span>Match columns</span>
            {schema?.can_manage_mapping ? (
              <button
                type="button"
                className="save-preset-button"
                disabled={busy}
                aria-disabled={Boolean(missingRequiredMappings.length)}
                onClick={missingRequiredMappings.length ? onMissingRequiredMappings : onSaveMapping}
              >
                {mappingSource === 'saved' ? 'Company Preset Saved' : 'Save Company Preset'}
              </button>
            ) : null}
          </div>
        </div>
        <p className="muted" style={{ padding: '1rem 1rem 0' }}>
          {savedMapping && mappingSource === 'saved'
            ? 'The saved company mapping was applied where source columns still matched.'
            : 'Safe field suggestions were applied. Confirm every mapping before validation.'}
        </p>
        {sections.map((section) => {
          const fields = mappingFields.filter((field) => field.section === section)
          return (
            <section className="match-section" key={section}>
              <div className="match-section-heading">
                <h5>{section}</h5>
                <span className="muted">
                  {fieldSectionDescriptions[section] || 'Match source columns to Kase fields.'}
                </span>
              </div>
              <div className="match-table-header">
                <span>Kase field</span>
                <span>Source column</span>
                <span></span>
              </div>
              {fields.map((field, index) => {
                const selected = selection[field.key] || []
                const subsection = shipmentSubsection(field)
                const showSubsection = subsection && subsection !== shipmentSubsection(fields[index - 1])
                return (
                  <React.Fragment key={field.key}>
                    {showSubsection ? <div className="match-subsection">{subsection}</div> : null}
                    <div className="match-row">
                      <div className="field-name">
                        <strong>{field.label}</strong>
                        {requiredBindingFields.has(field.key) ? (
                          <span className="required-marker">Required</span>
                        ) : field.required ? (
                          <span className="conditional-marker">Required</span>
                        ) : null}
                        {field.key === 'billing_account' ? (
                          <span className="conditional-marker">Required for Third Party</span>
                        ) : null}
                      </div>
                      <Select
                        aria-label={`Source column for ${field.label}`}
                        options={sourceOptions}
                        value={sourceOptions.filter((option) => selected.includes(option.value))}
                        onChange={(option: any) => {
                          const next = field.multiple_sources
                            ? (option || []).map((value: { value: number }) => value.value)
                            : option
                              ? [option.value]
                              : []
                          onSelectionChange(field.key, next)
                        }}
                        isMulti={field.multiple_sources}
                        isClearable
                        closeMenuOnSelect={!field.multiple_sources}
                        className="basic-multi-select"
                        isDisabled={busy}
                      />
                      <FontAwesomeIcon
                        className={`match-status-icon ${
                          selected.length ? '' : requiredBindingFields.has(field.key) ? 'required-missing' : 'ignored'
                        }`}
                        icon={selected.length ? faCheckDouble : faTimes}
                        title={
                          selected.length
                            ? 'Mapped'
                            : requiredBindingFields.has(field.key)
                              ? 'Required mapping missing'
                              : 'Not mapped'
                        }
                      />
                    </div>
                  </React.Fragment>
                )
              })}
            </section>
          )
        })}
        {selection.hot?.length ? (
          <div style={{ padding: '1rem' }}>
            <Form.Check
              checked={transformHot}
              disabled={busy}
              label="Any populated Hot source value means Yes"
              onChange={(event) => onTransformHotChange(event.target.checked)}
            />
          </div>
        ) : null}
        {skippedColumns.length ? (
          <div className="unmapped-source-columns">
            <div className="unmapped-source-heading">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>Unmapped source columns</span>
            </div>
            <p className="muted">These columns are present in the uploaded file and will be skipped.</p>
            <div className="unmapped-source-list">
              {skippedColumns.map((column) => (
                <span className="unmapped-source-chip" key={column.id}>
                  {column.header}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
    <div className="importer-footer">
      {mappingNotice ? (
        <span className="mapping-blocker-message" role="alert">
          {mappingNotice}
        </span>
      ) : null}
      <button type="button" className="plain-action" disabled={busy} onClick={onBack}>
        Back
      </button>
      <GlobalStyles.Button
        className="mapping-next-button"
        disabled={busy}
        aria-disabled={Boolean(missingRequiredMappings.length)}
        onClick={missingRequiredMappings.length ? onMissingRequiredMappings : onValidate}
      >
        {busy ? 'Validating...' : 'Validate complete file'}
      </GlobalStyles.Button>
    </div>
  </>
)

type EditingCell = { orderId: string; field: string } | null

type FieldControlProps = {
  order: CanonicalOrder
  field: SchemaField
  busy: boolean
  onChange: (value: string | boolean) => void
  autoFocus?: boolean
}

const FieldControl = ({ order, field, busy, onChange, autoFocus = false }: FieldControlProps) => {
  const value = orderFieldValue(order, field.key)
  const label = `${field.label} for ${orderLabel(order)}`
  if (field.choices) {
    return (
      <Form.Select
        aria-label={label}
        autoFocus={autoFocus}
        value={String(value || '')}
        disabled={busy}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">None</option>
        {field.choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </Form.Select>
    )
  }
  if (field.key === 'hot') {
    return (
      <Form.Select
        aria-label={label}
        autoFocus={autoFocus}
        value={value ? 'true' : 'false'}
        disabled={busy}
        onChange={(event) => onChange(event.target.value === 'true')}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </Form.Select>
    )
  }
  return (
    <Form.Control
      aria-label={label}
      autoFocus={autoFocus}
      type={field.key === 'ship_after' || field.key === 'ship_before' ? 'date' : 'text'}
      value={String(value ?? '')}
      disabled={busy}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

type ValidationStepProps = {
  result: ValidationResult
  dirty: boolean
  dirtyOrderIds: string[]
  fileIssues: UploadIssue[]
  fullscreen: boolean
  view: 'issues' | 'all'
  issueOrderCount: number
  allOrderCount: number
  reviewFields: SchemaField[]
  visibleOrders: CanonicalOrder[]
  editingCell: EditingCell
  busy: boolean
  page: number
  totalPages: number
  submissionReady: boolean
  onViewChange: (view: 'issues' | 'all') => void
  onToggleFullscreen: () => void
  onEditingCellChange: (cell: EditingCell) => void
  onUpdateOrderField: (orderId: string, field: string, value: string | boolean) => void
  onUpdateItem: (orderId: string, itemId: string, fields: Partial<Pick<CanonicalItem, 'sku' | 'quantity'>>) => void
  onDeleteItem: (orderId: string, itemId: string) => void
  onAddItem: (orderId: string) => void
  onQuantityError: () => void
  onToggleExcluded: (order: CanonicalOrder, excluded: boolean) => void
  onOpenOrderEditor: (order: CanonicalOrder) => void
  onPageChange: (page: number) => void
  onBack: () => void
  onRevalidate: () => void
  onSubmit: () => void
}

export const ValidationStep = ({
  result,
  dirty,
  dirtyOrderIds,
  fileIssues,
  fullscreen,
  view,
  issueOrderCount,
  allOrderCount,
  reviewFields,
  visibleOrders,
  editingCell,
  busy,
  page,
  totalPages,
  submissionReady,
  onViewChange,
  onToggleFullscreen,
  onEditingCellChange,
  onUpdateOrderField,
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  onQuantityError,
  onToggleExcluded,
  onOpenOrderEditor,
  onPageChange,
  onBack,
  onRevalidate,
  onSubmit,
}: ValidationStepProps) => {
  const renderEditableCell = (order: CanonicalOrder, field: SchemaField) => {
    const isEditing = editingCell?.orderId === order.id && editingCell.field === field.key
    const issues = fieldIssues(order.issues, field.key)
    const errors = issues.filter((issue) => issue.level === 'error')
    const warnings = issues.filter((issue) => issue.level === 'warning')
    const value = orderFieldValue(order, field.key)
    return (
      <td
        className={`validation-cell editable-cell ${isEditing ? 'editing' : ''} ${
          errors.length ? 'validation-error' : warnings.length ? 'validation-warning' : ''
        }`}
      >
        {isEditing ? (
          <div
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) onEditingCellChange(null)
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <FieldControl
              order={order}
              field={field}
              busy={busy}
              onChange={(nextValue) => onUpdateOrderField(order.id, field.key, nextValue)}
              autoFocus
            />
          </div>
        ) : (
          <button
            type="button"
            className="cell-edit-trigger"
            aria-label={`Edit ${field.label} for ${orderLabel(order)}`}
            onClick={() => onEditingCellChange({ orderId: order.id, field: field.key })}
          >
            <span
              className={`editable-cell-value ${value === null || value === undefined || value === '' ? 'empty' : ''}`}
            >
              {displayValue(value, field.choices)}
            </span>
          </button>
        )}
        {issues.map((issue, index) => (
          <div
            className={`field-error-message ${issue.level === 'warning' ? 'warning' : ''}`}
            key={`${issue.code}-${index}`}
          >
            {issue.message}
          </div>
        ))}
      </td>
    )
  }

  const renderEditableItemsCell = (order: CanonicalOrder) => {
    const isEditing = editingCell?.orderId === order.id && editingCell.field === 'items'
    const issues = order.issues.filter((issue) => issue.item_id || issue.field_path.startsWith('items'))
    const errors = issues.filter((issue) => issue.level === 'error')
    const warnings = issues.filter((issue) => issue.level === 'warning')
    return (
      <td
        className={`validation-cell items editable-cell ${isEditing ? 'editing' : ''} ${
          errors.length ? 'validation-error' : warnings.length ? 'validation-warning' : ''
        }`}
      >
        {isEditing ? (
          <div className="inline-items-editor" onClick={(event) => event.stopPropagation()}>
            {order.items.map((item, index) => (
              <div className="inline-item-row" key={item.id}>
                <Form.Control
                  autoFocus={index === 0}
                  aria-label={`SKU for ${orderLabel(order)}`}
                  value={item.sku}
                  disabled={busy}
                  onChange={(event) => onUpdateItem(order.id, item.id, { sku: event.target.value })}
                />
                <Form.Control
                  aria-label={`Quantity for ${item.sku}`}
                  min={1}
                  step={1}
                  type="number"
                  defaultValue={item.quantity}
                  disabled={busy}
                  onBlur={(event) => {
                    const quantity = parsePositiveInteger(event.target.value)
                    if (quantity === null) {
                      event.target.value = String(item.quantity)
                      onQuantityError()
                      return
                    }
                    if (quantity !== item.quantity) onUpdateItem(order.id, item.id, { quantity })
                  }}
                />
                <button
                  type="button"
                  className="inline-item-action"
                  aria-label={`Delete ${item.sku}`}
                  onClick={() => onDeleteItem(order.id, item.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
            <button type="button" className="add-item-button" onClick={() => onAddItem(order.id)}>
              <FontAwesomeIcon icon={faPlus} /> Add Item
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="cell-edit-trigger"
            aria-label={`Edit items for ${orderLabel(order)}`}
            onClick={() => onEditingCellChange({ orderId: order.id, field: 'items' })}
          >
            <span className="item-lines">
              {order.items.map((item) => (
                <span className="item-line" key={item.id}>
                  <strong>{item.sku}</strong>
                  <span>Qty {item.quantity}</span>
                </span>
              ))}
            </span>
          </button>
        )}
        {issues.map((issue, index) => (
          <div
            className={`field-error-message ${issue.level === 'warning' ? 'warning' : ''}`}
            key={`${issue.code}-${index}`}
          >
            {issue.message}
          </div>
        ))}
      </td>
    )
  }

  const renderStatus = (order: CanonicalOrder) => {
    const status = order.excluded
      ? 'excluded'
      : dirtyOrderIds.includes(order.id)
        ? 'pending'
        : order.issues.some((issue) => issue.level === 'error')
          ? 'failing'
          : order.issues.some((issue) => issue.level === 'warning')
            ? 'warning'
            : 'valid'
    if (status === 'failing') {
      return (
        <ValidationStatusStyled className="red">
          <FontAwesomeIcon icon={faTimes} /> <span>Failed</span>
        </ValidationStatusStyled>
      )
    }
    if (status === 'pending' || status === 'warning') {
      return (
        <ValidationStatusStyled className="yellow">
          <FontAwesomeIcon icon={faExclamationTriangle} /> <span>{status === 'pending' ? 'Pending' : 'Warning'}</span>
        </ValidationStatusStyled>
      )
    }
    if (status === 'excluded') {
      return (
        <ValidationStatusStyled>
          <FontAwesomeIcon icon={faTrash} /> <span>Discarded</span>
        </ValidationStatusStyled>
      )
    }
    return (
      <ValidationStatusStyled className="green">
        <FontAwesomeIcon icon={faCheckDouble} /> <span>Valid</span>
      </ValidationStatusStyled>
    )
  }

  return (
    <div className="validate-workspace">
      <div className="importer-body">
        <div className="summary-grid">
          <div className="summary-card">
            <strong>{result.summary.source_rows}</strong>
            <span className="muted">source rows</span>
          </div>
          <div className="summary-card">
            <strong>{result.summary.orders}</strong>
            <span className="muted">grouped orders</span>
          </div>
          <div className="summary-card">
            <strong>{result.summary.blocking_errors}</strong>
            <span className="muted">blocking errors</span>
          </div>
        </div>
        {dirty ? (
          <div className="error-callout">
            <strong>Changes need validation.</strong>
            <p className="muted">Existing messages are from the previous validation. Revalidate before importing.</p>
          </div>
        ) : result.summary.blocking_errors ? (
          <div className="error-callout">
            <strong>Resolve errors before importing</strong>
            <p className="muted">Edit highlighted values, then revalidate the complete import.</p>
          </div>
        ) : null}
        {fileIssues.map((issue, index) => (
          <div
            className={`field-error-message ${issue.level === 'warning' ? 'warning' : ''}`}
            key={`${issue.code}-${index}`}
          >
            {issue.message}
          </div>
        ))}
        <div className={`validation-panel ${fullscreen ? 'fullscreen' : ''}`}>
          <div className="validation-header">
            <span>Validate data</span>
            <div className="preset-actions" role="tablist" aria-label="Order review filters">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'issues'}
                className={`plain-action ${view === 'issues' ? 'selected' : ''}`}
                onClick={() => onViewChange('issues')}
              >
                Issues {issueOrderCount}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'all'}
                className={`plain-action ${view === 'all' ? 'selected' : ''}`}
                onClick={() => onViewChange('all')}
              >
                All Orders {allOrderCount}
              </button>
              <button type="button" className="fullscreen-toggle" onClick={onToggleFullscreen}>
                <FontAwesomeIcon icon={fullscreen ? faCompressArrowsAlt : faExpandArrowsAlt} />
                <span>{fullscreen ? 'Exit Full Screen' : 'Full Screen'}</span>
              </button>
            </div>
          </div>
          <div className="table-wrap" role="tabpanel">
            <table>
              <thead>
                <tr>
                  <th className="validation-cell compact">Include</th>
                  {reviewFields.map((field) => (
                    <th className="validation-cell" key={field.key}>
                      {field.label}
                    </th>
                  ))}
                  <th className="validation-cell items" style={{ textAlign: 'center' }}>
                    Items
                  </th>
                  <th className="validation-cell compact">Source Rows</th>
                  <th className="status-column">Status</th>
                  <th className="validation-cell actions action-column">Edit</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="validation-cell compact">
                      <Form.Check
                        aria-label={`Include ${orderLabel(order)}`}
                        checked={!order.excluded}
                        disabled={busy}
                        onChange={(event) => onToggleExcluded(order, !event.target.checked)}
                      />
                    </td>
                    {reviewFields.map((field) => (
                      <React.Fragment key={field.key}>{renderEditableCell(order, field)}</React.Fragment>
                    ))}
                    {renderEditableItemsCell(order)}
                    <td className="validation-cell compact">{order.source_rows.join(', ')}</td>
                    <td className="status-column">
                      {renderStatus(order)}
                      {unmatchedOrderIssues(order, reviewFields).map((issue, index) => (
                        <div
                          className={`field-error-message ${issue.level === 'warning' ? 'warning' : ''}`}
                          key={`${issue.code}-${index}`}
                        >
                          {issue.message}
                        </div>
                      ))}
                    </td>
                    <td className="validation-cell actions action-column">
                      <div className="row-action-cell">
                        <button
                          type="button"
                          className="row-action-button"
                          aria-label={`Edit order ${orderLabel(order)}`}
                          onClick={() => onOpenOrderEditor(order)}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!visibleOrders.length ? (
            <p className="muted" style={{ padding: '1rem' }}>
              No orders in this view.
            </p>
          ) : null}
          {totalPages > 1 ? (
            <div className="importer-footer" aria-label="Order review pages">
              <button
                type="button"
                className="plain-action"
                disabled={page === 1}
                onClick={() => onPageChange(Math.max(1, page - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="plain-action"
                disabled={page === totalPages}
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div className="importer-footer">
        <button type="button" className="plain-action" disabled={busy} onClick={onBack}>
          Back
        </button>
        {!submissionReady ? (
          <GlobalStyles.Button disabled={busy} onClick={onRevalidate}>
            {busy ? 'Validating...' : 'Revalidate all changes'}
          </GlobalStyles.Button>
        ) : (
          <GlobalStyles.Button disabled={busy} onClick={onSubmit}>
            {busy ? 'Submitting...' : 'Submit Import'}
          </GlobalStyles.Button>
        )}
      </div>
    </div>
  )
}

type OrderEditDrawerProps = {
  order: CanonicalOrder
  sections: string[]
  reviewFields: SchemaField[]
  busy: boolean
  drawerRef: React.RefObject<HTMLDivElement | null>
  draftItemQuantities: Record<string, string>
  hasDraftChanges: boolean
  hasInvalidDraftQuantity: boolean
  onClose: () => void
  onUpdateField: (field: string, value: string | boolean) => void
  onUpdateItemSku: (itemId: string, sku: string) => void
  onUpdateItemQuantity: (itemId: string, quantity: string) => void
  onDeleteItem: (itemId: string) => void
  onAddItem: () => void
  onToggleExcluded: () => void
  onSave: () => void
}

export const OrderEditDrawer = ({
  order,
  sections,
  reviewFields,
  busy,
  drawerRef,
  draftItemQuantities,
  hasDraftChanges,
  hasInvalidDraftQuantity,
  onClose,
  onUpdateField,
  onUpdateItemSku,
  onUpdateItemQuantity,
  onDeleteItem,
  onAddItem,
  onToggleExcluded,
  onSave,
}: OrderEditDrawerProps) => (
  <div className="drawer-backdrop" onClick={onClose}>
    <div
      ref={drawerRef}
      className="edit-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-editor-title"
      tabIndex={-1}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="edit-drawer-header">
        <div>
          <strong id="order-editor-title">Edit order {orderLabel(order)}</strong>
          <div className="muted">Changes must pass complete backend validation before import.</div>
        </div>
        <button type="button" className="plain-action" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="edit-drawer-body">
        {sections
          .filter((section) => section !== 'Items')
          .map((section) => {
            const fields = reviewFields.filter((field) => field.section === section)
            return (
              <div className="edit-section" key={section}>
                <h5>{section}</h5>
                {Array.from(new Set(fields.map(shipmentSubsection))).map((subsection) => {
                  const subsectionFields = fields.filter((field) => shipmentSubsection(field) === subsection)
                  return (
                    <React.Fragment key={subsection || section}>
                      {subsection ? <h6 className="edit-subsection">{subsection}</h6> : null}
                      <div className="edit-grid">
                        {subsectionFields.map((field) => (
                          <Form.Group key={field.key} className={field.key === 'order_notes' ? 'wide' : ''}>
                            <Form.Label>
                              {field.label} {field.required ? <span className="form-required">Required</span> : null}
                              {field.key === 'billing_account' ? (
                                <span className="conditional-label">Required for Third Party</span>
                              ) : null}
                            </Form.Label>
                            <FieldControl
                              order={order}
                              field={field}
                              busy={busy}
                              onChange={(value) => onUpdateField(field.key, value)}
                            />
                          </Form.Group>
                        ))}
                      </div>
                    </React.Fragment>
                  )
                })}
              </div>
            )
          })}
        <div className="edit-section">
          <h5>Items</h5>
          <table className="drawer-items-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Quantity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Form.Control
                      aria-label={`SKU for ${orderLabel(order)}`}
                      value={item.sku}
                      onChange={(event) => onUpdateItemSku(item.id, event.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      aria-label={`Quantity for ${item.sku}`}
                      min={1}
                      step={1}
                      type="number"
                      value={draftItemQuantities[item.id] ?? String(item.quantity)}
                      onChange={(event) => onUpdateItemQuantity(item.id, event.target.value)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="inline-item-action"
                      aria-label={`Delete ${item.sku}`}
                      onClick={() => onDeleteItem(item.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="add-item-button" onClick={onAddItem}>
            <FontAwesomeIcon icon={faPlus} /> Add Item
          </button>
        </div>
      </div>
      <div className="edit-drawer-footer">
        <button type="button" className="plain-action" onClick={onToggleExcluded}>
          {order.excluded ? 'Restore Order' : 'Discard Order'}
        </button>
        <button type="button" className="plain-action" onClick={onClose}>
          Cancel
        </button>
        <GlobalStyles.Button disabled={!hasDraftChanges || hasInvalidDraftQuantity} onClick={onSave}>
          Save Changes
        </GlobalStyles.Button>
      </div>
    </div>
  </div>
)

type SubmittedStepProps = {
  file: File | null
  submission: SubmissionResult | null
  onUploadAnotherFile: () => void
}

export const SubmittedStep = ({ file, submission, onUploadAnotherFile }: SubmittedStepProps) => (
  <>
    <div className="importer-body">
      <div className="success-note">
        <strong>Order import submitted</strong>
        <p style={{ margin: '0.35rem 0 0' }}>
          {file?.name || 'The order file'} was queued for processing. You will receive results by email.
        </p>
      </div>
      {submission ? (
        <div className="summary-grid" style={{ marginTop: '1rem' }}>
          <div className="summary-card">
            <strong>{submission.summary.orders}</strong>
            <span className="muted">orders submitted</span>
          </div>
          <div className="summary-card">
            <strong>{submission.summary.items}</strong>
            <span className="muted">items submitted</span>
          </div>
          <div className="summary-card">
            <strong>Saved</strong>
            <span className="muted">normalized CSV document</span>
          </div>
        </div>
      ) : null}
    </div>
    <div className="importer-footer">
      <GlobalStyles.Button onClick={onUploadAnotherFile}>Upload Another File</GlobalStyles.Button>
    </div>
  </>
)
